import React, { useEffect, useRef, useState } from "react";
import {
  webLoadingFalse,
  webNavigate,
} from "../../services/navigate.service";
import PlusIcon from "../../images/PlusIcon";
import CloseIcon from "../../images/CloseIcon";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import {
  extractValues,
  updateUrlParams,
} from "../../validation/ValidationInput";
import {
  deleteGroupTax,
  deleteTax,
  getGroupTax,
  getTax,
} from "../../services/tax.services";
import { useResponsive } from "../../hook/useResponsive";
import { showApiError } from "../../services/apiError";
import TagStatus from "../../components/TagStatus";
import { ConfirmSheet } from "../../components/mobile";
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
  formatDayMonth,
  t,
} from "../../components/list";
interface TaxRow {
  id: string;
  taxCode: string;
  taxName: string;
  taxGroup: { groupName: string } | null;
  primaryRate: string;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
}
interface TaxGroupRow {
  id: string;
  groupName: string;
  groupCode: string;
  isActive: boolean;
}

/** `valid_from` / `valid_to` are date columns ("2025-01-03"); new Date() would read them as UTC midnight. */
const formatBusinessDay = (value: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
  if (!match) return value ? formatDayMonth(value) : "—";
  return formatDayMonth(new Date(+match[1], +match[2] - 1, +match[3]));
};

/** Both list endpoints return a bare array with no count, so a tab loads whole and pages client-side. */
const ALL_ROWS_LIMIT = 1000;

const includesText = (value: any, needle: string) =>
  String(value ?? "").toLowerCase().includes(needle);
const compareText = (a: any, b: any) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true, sensitivity: "base" });

const renderStatus = (isActive: boolean, language: any) => {
  const value = isActive ? "Active" : "InActive";
  return <TagStatus value={value} label={t(language, value, value)} />;
};

const Tax = () => {
  const preLanguage = localStorage.getItem("language");
  const { isTouchLayout } = useResponsive();
  const [language, setLanguage] = useState<any>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>("");
  const [nameDelete, setNameDelete] = useState<string>("");
  // Read before the list mounts so `key={tab}` does not remount it on load.
  const [tab, setTab] = useState<string>(() => {
    const filterParams: any = extractValues(location.search.split("&"));
    return filterParams.tab === "group" ? "group" : "tax";
  });
  /** The active tab's whole list, fetched once; cleared after a delete. */
  const allRows = useRef<{ tab: string; rows: Promise<any[]> } | null>(null);
  const emptyState = {
    title:
      language && language?.["No TAX Found"]
        ? language?.["No TAX Found"]
        : "No TAX Found",
    desc:
      language && language?.["A short description about this."]
        ? language?.["A short description about this."]
        : "A short description about this.",
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  // Back / Forward on this page: re-read ?tab; `key={tab}` remounts the list.
  useEffect(() => {
    const path = location.pathname;
    const handlePopState = () => {
      if (location.pathname !== path) return;
      const filterParams: any = extractValues(location.search.split("&"));
      setTab(filterParams.tab === "group" ? "group" : "tax");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const loadAllRows = (forTab: string): Promise<any[]> => {
    if (allRows.current?.tab !== forTab) {
      const params = { page: 1, limit: ALL_ROWS_LIMIT, searchValue: "" };
      const rows = (forTab === "tax" ? getTax({ ...params, sort: "DESC" }) : getGroupTax({ ...params, sort: "ASC" }))
        .then((response: any) => (Array.isArray(response.data) ? response.data : []));
      const entry = { tab: forTab, rows };
      allRows.current = entry;
      // A failed load is retried on the next fetch.
      rows.catch(() => {
        if (allRows.current === entry) allRows.current = null;
      });
    }
    return allRows.current!.rows;
  };

  /** One page of the active tab, searched (as the endpoints did), sorted and sliced in memory. */
  const fetchRows = async (query: ListQuery) => {
    webLoadingFalse();
    const all = await loadAllRows(tab);
    const needle = query.search.trim().toLowerCase();
    const matched =
      tab === "tax"
        ? (all as TaxRow[])
            .filter((r) => !needle || includesText(r.taxCode, needle) || includesText(r.taxName, needle))
            .sort((a, b) => compareText(b.taxCode, a.taxCode))
        : (all as TaxGroupRow[])
            .filter((r) => !needle || includesText(r.groupName, needle))
            .sort((a, b) =>
              query.sortDir === "ASC" ? compareText(a.groupName, b.groupName) : compareText(b.groupName, a.groupName),
            );
    const start = (query.page - 1) * query.limit;
    return { rows: matched.slice(start, start + query.limit), count: matched.length };
  };

  /** Switching tabs resets the list (page 1, no search); `key={tab}` remounts it so it re-reads the URL. */
  const handleChangeTab = (value: string) => {
    if (value === tab) return;
    setTab(value);
    webNavigate(updateUrlParams({ tab: value, page: 1, limit: 50 }));
  };

  const goToRecord = (id: string) =>
    webNavigate("/" + preLanguage + (tab === "tax" ? "/tax/" : "/grouptax/") + id);

  const handleShowDelete = (row: any) => {
    setShowDelete(true);
    setIdDelete(String(row.id));
    setNameDelete(tab === "tax" ? row.taxCode ?? "" : row.groupName ?? "");
  };
  const handleDeleteTax = async () => {
    try {
      if (tab === "tax") {
        await deleteTax(idDelete);
      } else {
        await deleteGroupTax(idDelete);
      }
    } catch (error) {
      showApiError(error, { language });
    } finally {
      allRows.current = null;
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const taxColumns: ListColumn<TaxRow>[] = [
    {
      key: "taxCode",
      label: "Code",
      width: 160,
      hideable: false,
      render: cells.primary((r) => r.taxCode),
      mobile: "title",
      onClick: (r) => goToRecord(String(r.id)),
    },
    {
      key: "title",
      label: "Title",
      width: 150,
      value: (r) => `${r.primaryRate} % RATE`,
      mobile: "sheet",
    },
    {
      key: "group",
      label: "Group",
      width: 180,
      value: (r) => r.taxGroup?.groupName ?? "",
      mobile: "subtitle",
    },
    {
      key: "value",
      label: "Value",
      width: 100,
      value: (r) => `${r.primaryRate} %`,
      mobile: "subtitle",
    },
    {
      key: "validFrom",
      label: "Valid From",
      width: 130,
      value: (r) => formatBusinessDay(r.validFrom),
      mobile: "sheet",
    },
    {
      key: "validTo",
      label: "Valid Until",
      width: 130,
      value: (r) => formatBusinessDay(r.validTo),
      mobile: "sheet",
    },
    {
      key: "status",
      label: "Status",
      width: 120,
      render: (r, ctx) => renderStatus(!!r.isActive, ctx.language),
      mobile: "trailing",
    },
  ];

  const groupColumns: ListColumn<TaxGroupRow>[] = [
    {
      key: "groupName",
      label: "Group Name",
      width: 260,
      hideable: false,
      sortable: true,
      render: cells.primary((r) => r.groupName),
      mobile: "title",
      onClick: (r) => goToRecord(String(r.id)),
    },
    { key: "groupCode", label: "Group Code", width: 200, mobile: "subtitle" },
    {
      key: "status",
      label: "Status",
      width: 120,
      render: (r, ctx) => renderStatus(!!r.isActive, ctx.language),
      mobile: "trailing",
    },
  ];

  const rowActions: ListRowAction<any>[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIconV2 />,
      sheetIcon: "inventory",
      onRun: (r) => goToRecord(String(r.id)),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteIconV2 />,
      sheetIcon: "close",
      danger: true,
      onRun: (r) => handleShowDelete(r),
    },
  ];

  const tabButton = (value: string, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === value}
      style={{
        color: tab === value ? "#FFFFFF" : "#6A7878",
        padding: "6px 14px",
        borderRadius: "30px",
        backgroundColor: tab === value ? "#0D5759" : "transparent",
        border: tab === value ? "1px solid transparent" : "1px solid #C9D7D8",
      }}
      onClick={() => handleChangeTab(value)}
      className="cursor-pointer text-[14px] leading-[130%] font-medium font-inter"
    >
      <span
        style={{
          fontSize: "14px",
          fontWeight: "500",
          lineHeight: "21px",
          whiteSpace: "nowrap",
          color: tab === value ? "#FFFFFF" : "#6A7878",
        }}
      >
        {label}
      </span>
    </button>
  );

  return (
    <>
      <div
        className="h-full relative"
        style={{
          height: "100%",
          overflowY: isTouchLayout ? "auto" : undefined,
          paddingBottom: isTouchLayout ? "24px" : undefined,
          display: isTouchLayout ? undefined : "flex",
          flexDirection: isTouchLayout ? undefined : "column",
          minHeight: 0,
        }}
      >
        <div style={{ flex: "none" }}>
          <p
            style={{ color: "#222525" }}
            className="text-[20px] leading-[130%] font-medium font-inter"
          >
            {language && language?.["TAX registration"]
              ? language?.["TAX registration"]
              : "TAX registration"}
          </p>
          <div
            className="flex items-center justify-between"
            style={{ marginTop: "12px", flexWrap: "wrap", gap: "12px" }}
          >
            <div
              className="flex items-center"
              style={{ gap: "12px" }}
              role="tablist"
            >
              {tabButton(
                "tax",
                language && language?.["TAX"] ? language?.["TAX"] : "TAX"
              )}
              {tabButton(
                "group",
                language && language?.["TAX Group"]
                  ? language?.["TAX Group"]
                  : "TAX Group"
              )}
            </div>
            <div
              className="cursor-pointer flex items-center justify-center"
              style={{
                gap: "8px",
                padding: "8px 24px 8px 16px",
                backgroundColor: "#0F7679",
                borderRadius: "30px",
              }}
              onClick={() => {
                tab === "tax"
                  ? webNavigate("/" + preLanguage + "/tax/new")
                  : webNavigate("/" + preLanguage + "/grouptax/new");
              }}
            >
              <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
                <PlusIcon />
              </div>
              {tab === "tax" ? (
                <p
                  style={{ color: "#FFFFFF" }}
                  className="text-[14px] leading-[130%] font-medium font-inter"
                >
                  {language && language?.["Add TAX"]
                    ? language?.["Add TAX"]
                    : "Add TAX"}
                </p>
              ) : (
                <p
                  style={{ color: "#FFFFFF" }}
                  className="text-[14px] leading-[130%] font-medium font-inter"
                >
                  {language && language?.["Add TAX Group"]
                    ? language?.["Add TAX Group"]
                    : "Add TAX Group"}
                </p>
              )}
            </div>
          </div>
        </div>
        <ListScreen
          key={tab}
          ref={listRef}
          language={language}
          columns={tab === "tax" ? taxColumns : groupColumns}
          fetchRows={fetchRows}
          searchPlaceholder="Search"
          searchInputRef={searchInputRef}
          defaultSort={tab === "group" ? { sortBy: "groupName", sortDir: "DESC" } : undefined}
          rowActions={rowActions}
          emptyState={emptyState}
          extraUrlParams={{ tab }}
        />
      </div>
      {isTouchLayout ? (
        <ConfirmSheet
          open={showDelete}
          title={
            language && language?.["Delete Item"]
              ? language?.["Delete Item"]
              : "Delete Item"
          }
          message={
            <>
              {language && language?.["You will permanently delete the tax"]
                ? language?.["You will permanently delete the tax"]
                : "You will permanently delete the tax"}
              {nameDelete && (
                <div style={{ marginTop: "4px", fontWeight: 500, color: "#222525" }}>
                  {nameDelete}
                </div>
              )}
            </>
          }
          confirmLabel={language && language?.["Delete"] ? language?.["Delete"] : "Delete"}
          cancelLabel={language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel"}
          onConfirm={handleDeleteTax}
          onCancel={() => setShowDelete(false)}
        />
      ) : (
        <div
          className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
            } transition-opacity duration-300 ease-in-out`}
          style={{
            backgroundColor: "#22252552",
            zIndex: "200",
            transition: "opacity 0.3s ease, visibility 0.3s ease",
            width: "100vw",
            height: "100vh",
            left: 0,
            top: 0,
          }}
        >
          <div
            className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"
              }`}
            style={{
              padding: "24px",
              borderRadius: "24px",
              backgroundColor: "#FFFFFF",
              width: "530px",
              transition: "transform 0.3s ease, opacity 0.3s ease",
            }}
          >
            <div className="flex items-center justify-between">
              <p
                style={{ fontSize: "20px", color: "#222525" }}
                className="font-semibold font-inter leading-[24px]"
              >
                {language && language?.["Delete Item"]
                  ? language?.["Delete Item"]
                  : "Delete Item"}
              </p>
              <div
                onClick={() => setShowDelete(false)}
                className="flex items-center justify-center cursor-pointer"
                style={{
                  width: "24px",
                  height: "24px",
                }}
              >
                <CloseIcon />
              </div>
            </div>
            <div
              className="flex items-center justify-between"
              style={{ marginTop: "12px" }}
            >
              <p
                className="text-[14px]  font-inter leading-[24px]"
                style={{ color: "#6A7878" }}
              >
                {language && language?.["You will permanently delete the tax"]
                  ? language?.["You will permanently delete the tax"]
                  : "You will permanently delete the tax"}
              </p>
              {/* <span className="text-main">[sale ID]</span> sale. */}
            </div>
            <div
              className="flex items-center justify-end"
              style={{ marginTop: "24px", gap: "12px" }}
            >
              <div
                onClick={() => setShowDelete(false)}
                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                style={{
                  borderRadius: "40px",
                  padding: "10px 24px",
                  border: "1px solid #C9D7D8",
                  color: "#222525",
                }}
              >
                {language && language?.["Cancel"]
                  ? language?.["Cancel"]
                  : "Cancel"}
              </div>
              <div
                onClick={handleDeleteTax}
                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                style={{
                  borderRadius: "40px",
                  padding: "10px 24px",
                  backgroundColor: "#BA0E14",
                  color: "#FFFFFF",
                }}
              >
                {language && language?.["Delete"]
                  ? language?.["Delete"]
                  : "Delete"}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Tax;
